import { UiStack } from '@experiences/ui-common';
import {
    ApStepper,
    ApStepperStep,
} from '@uipath/portal-shell-react';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import type { StepComponentProps } from './constants';
import { GovernancePolicyCreateEnforcementStep } from './steps/enforcement/GovernancePolicyCreateEnforcementStep';
import { GovernancePolicyCreateFinishStep } from './steps/finish/GovernancePolicyCreateFinishStep';
import { GovernancePolicyCreateScopeStep } from './steps/scope/GovernancePolicyCreateScopeStep';
import { GovernancePolicyCreateStartStep } from './steps/start/GovernancePolicyCreateStartStep';
import { GovernancePolicyStepContainer } from './subcomponent/GovernancePolicyStepContainer';

type StepDefinition = {
    stepKey: string;
    titleKey: string;
    descriptionKey: string;
    component: React.ComponentType<StepComponentProps>;
};

// Define steps outside the component with localization keys
const steps: StepDefinition[] = [
    {
        stepKey: 'CLIENT_GOVERNANCE_STEP_START',
        titleKey: 'CLIENT_GOVERNANCE_STEP_START_TITLE',
        descriptionKey: 'CLIENT_GOVERNANCE_STEP_START_DESCRIPTION',
        component: GovernancePolicyCreateStartStep,
    },
    {
        stepKey: 'CLIENT_GOVERNANCE_STEP_SCOPE',
        titleKey: 'CLIENT_GOVERNANCE_STEP_SCOPE_TITLE',
        descriptionKey: 'CLIENT_GOVERNANCE_STEP_SCOPE_DESCRIPTION',
        component: GovernancePolicyCreateScopeStep,
    },
    {
        stepKey: 'CLIENT_GOVERNANCE_STEP_ENFORCEMENT',
        titleKey: 'CLIENT_GOVERNANCE_STEP_ENFORCEMENT_TITLE',
        descriptionKey: 'CLIENT_GOVERNANCE_STEP_ENFORCEMENT_DESCRIPTION',
        component: GovernancePolicyCreateEnforcementStep,
    },
    {
        stepKey: 'CLIENT_GOVERNANCE_STEP_FINISH',
        titleKey: 'CLIENT_GOVERNANCE_STEP_FINISH_TITLE',
        descriptionKey: 'CLIENT_GOVERNANCE_STEP_FINISH_DESCRIPTION',
        component: GovernancePolicyCreateFinishStep,
    },
];

export const GovernanceStepperComponent: React.FC = () => {
    const [ currentStep, setCurrentStep ] = useState(0);
    const { formatMessage: translate } = useIntl();

    const StepComponent = steps[currentStep].component;

    const nextStep = () => {
        setCurrentStep((prev) => prev + 1);
    };

    const prevStep = () => setCurrentStep((prev) => prev - 1);

    return (
        <UiStack
            direction="row"
            h="100%"
        >
            <UiStack
                direction="column"
                w='220px'
                align='start'
            >
                <ApStepper
                    vertical
                    activeStep={currentStep}
                    data-cy='governance-stepper'>
                    {steps.map((step, index) => (
                        <ApStepperStep
                            key={index}
                            label={translate({ id: step.stepKey })} />
                    ))}
                </ApStepper>
            </UiStack>
            <UiStack
                grow={1}
                direction="column"
            >
                <GovernancePolicyStepContainer
                    title={translate({ id: steps[currentStep].titleKey })}
                    description={translate({ id: steps[currentStep].descriptionKey })}>
                    <StepComponent
                        onNext={nextStep}
                        onBack={prevStep}
                    />
                </GovernancePolicyStepContainer>
            </UiStack>
        </UiStack>
    );
};
